import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../App';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { Heart } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

const LoginPage = () => {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [seeding, setSeeding] = useState(false);
  const { login, user } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (user) {
      navigate('/dashboard');
    }
  }, [user, navigate]);

  const handleSeedData = async () => {
    setSeeding(true);
    try {
      const response = await axios.post(`${API}/seed-demo-data`);
      if (response.data.seeded) {
        toast.success('Demo data seeded successfully!');
        toast.info('Login with: dr.andrea@clinic.com / demo123');
      } else {
        toast.info('Demo data already exists');
      }
    } catch (error) {
      toast.error('Failed to seed demo data');
    } finally {
      setSeeding(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) {
      toast.error('Please enter email and password');
      return;
    }
    
    setLoading(true);
    const result = await login(email, password);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  const handleDemoLogin = async (role) => {
    const credentials = {
      doctor: { email: 'dr.andrea@clinic.com', password: 'demo123' },
      nurse: { email: 'nurse.mica@clinic.com', password: 'demo123' },
      cashier: { email: 'cashier.leo@clinic.com', password: 'demo123' },
    };
    
    setEmail(credentials[role].email);
    setPassword(credentials[role].password);
    
    setLoading(true);
    const result = await login(credentials[role].email, credentials[role].password);
    setLoading(false);
    
    if (result.success) {
      navigate('/dashboard');
    }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-md space-y-6 animate-fade-in">

        {/* Logo */}
        <div className="text-center">
          <img
            src="/logo.jpg"
            alt="Clinic Care OS"
            className="mx-auto h-16 w-auto mb-2"
          />
          <p className="text-sm text-muted-foreground">Club Asia</p>
        </div>
        {/* Login Card */}
        <Card className="border-border shadow-card">
          <CardHeader className="text-center">
            <CardTitle className="font-heading text-xl">Welcome Back</CardTitle>
            <CardDescription>Sign in to your account</CardDescription>
          </CardHeader>
          <CardContent>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="space-y-2">
                <Label htmlFor="email">Email</Label>
                <Input
                  id="email"
                  type="email"
                  placeholder="Enter your email"
                  value={email}
                  onChange={(e) => setEmail(e.target.value)}
                  className="h-11"
                  data-testid="login-email"
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="password">Password</Label>
                <Input
                  id="password"
                  type="password"
                  placeholder="Enter your password"
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="h-11"
                  data-testid="login-password"
                />
              </div>
              <Button 
                type="submit" 
                className="w-full h-11 rounded-full"
                disabled={loading}
                data-testid="login-submit"
              >
                {loading ? 'Signing in...' : 'Sign In'}
              </Button>
            </form>

            {/* Demo Login */}
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-xs text-muted-foreground text-center mb-4">
                Demo Accounts (click to login)
              </p>
              <div className="grid grid-cols-3 gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin('doctor')}
                  disabled={loading}
                  data-testid="demo-login-doctor"
                >
                  Doctor
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin('nurse')}
                  disabled={loading}
                  data-testid="demo-login-nurse"
                >
                  Nurse
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => handleDemoLogin('cashier')}
                  disabled={loading}
                  data-testid="demo-login-cashier"
                >
                  Cashier
                </Button>
              </div>
              <Button
                variant="ghost"
                size="sm"
                className="w-full mt-3 text-xs"
                onClick={handleSeedData}
                disabled={seeding}
                data-testid="seed-demo-data"
              >
                {seeding ? 'Seeding...' : 'Initialize Demo Data'}
              </Button>
            </div>
          </CardContent>
        </Card>

        {/* Footer */}
        <p className="text-center text-xs text-muted-foreground">
          A simple clinic organizer for better patient care
        </p>
      </div>
    </div>
  );
};

export default LoginPage;
